import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";

function SeedRow({ team, fallback }) {
    if (!team)
        return (
            <div className="flex items-center gap-2.5 py-2.5 px-3">
                <span className="w-6 h-6 rounded border border-dashed border-[#262638] flex items-center justify-center text-slate-600 text-xs font-stat shrink-0">
                    ?
                </span>
                <span className="text-xs text-slate-500 font-display uppercase tracking-wider truncate">
                    {fallback || "TBD"}
                </span>
            </div>
        );
    return (
        <div className="flex items-center gap-2.5 py-2.5 px-3">
            <span
                data-testid={`seed-badge-${team.seed}`}
                className="w-6 h-6 rounded bg-[#FF5500]/15 border border-[#FF5500]/50 text-[#FF5500] flex items-center justify-center text-xs font-stat font-bold shrink-0"
            >
                {team.seed}
            </span>
            <TeamCrest team={team} size="xs" />
            <div className="min-w-0">
                <p className="font-display font-bold uppercase text-sm text-white truncate leading-tight">{team.name}</p>
                <p className="text-[10px] font-stat text-slate-500">
                    {team.w}W – {team.l}L
                </p>
            </div>
        </div>
    );
}

function MatchupBox({ match, testid, gold }) {
    return (
        <div
            data-testid={testid}
            className={`bg-[#1A1A26] rounded-2xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FF5500] hover:shadow-[0_0_15px_rgba(255,85,0,0.3)] ${
                gold
                    ? "border-[#FFB800]/60 shadow-[0_0_40px_-10px_rgba(255,184,0,0.35)]"
                    : "border-white/10"
            }`}
        >
            <div
                className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] ${
                    gold ? "bg-[#FFB800]/10 text-[#FFB800]" : "bg-[#12121A] text-slate-400"
                }`}
            >
                {match.label}
                {match.note ? <span className="text-slate-500 normal-case tracking-normal"> • {match.note}</span> : null}
            </div>
            <SeedRow team={match.home} fallback={match.home_note} />
            <div className="mx-3 border-t border-dashed border-[#262638]" />
            <SeedRow team={match.away} fallback={match.away_note} />
        </div>
    );
}

function RoundColumn({ title, testid, topAnchor, children }) {
    return (
        <div
            data-testid={testid}
            className={`relative flex flex-col min-h-[520px] ${
                topAnchor ? "justify-start pt-16" : "justify-center"
            }`}
        >
            <p className="absolute top-0 left-0 right-0 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 text-center">
                {title}
            </p>
            {children}
        </div>
    );
}

function Connector({ dir }) {
    return (
        <span
            aria-hidden="true"
            className={`absolute top-1/2 -translate-y-1/2 w-6 h-px ${
                dir === "right"
                    ? "-right-6 bg-gradient-to-r from-[#FF5500]/80 to-[#262638]"
                    : "-left-6 bg-gradient-to-l from-[#FF5500]/80 to-[#262638]"
            }`}
            style={{ boxShadow: "0 0 6px rgba(255,85,0,0.35)" }}
        />
    );
}

function ElbowLeft() {
    return (
        <>
            <span aria-hidden="true" className="absolute -left-6 top-[124px] w-3 h-px bg-[#3E3E52]" />
            <span aria-hidden="true" className="absolute -left-3 top-[124px] bottom-1/2 w-px bg-[#3E3E52]" />
            <span aria-hidden="true" className="absolute -left-3 top-1/2 w-3 h-px bg-[#3E3E52]" />
        </>
    );
}

function ElbowRight() {
    return (
        <>
            <span aria-hidden="true" className="absolute -left-3 top-[124px] w-3 h-px bg-[#3E3E52]" />
            <span aria-hidden="true" className="absolute -left-3 top-[124px] bottom-1/2 w-px bg-[#3E3E52]" />
            <span aria-hidden="true" className="absolute -left-6 top-1/2 w-3 h-px bg-[#3E3E52]" />
        </>
    );
}

export default function PlayoffBracket() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/playoffs")
            .then((r) => !cancelled && setData(r.data))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    if (!data)
        return (
            <p className="mt-16 text-slate-500 font-stat text-sm" data-testid="playoffs-loading">
                Loading bracket…
            </p>
        );

    return (
        <div data-testid="playoff-bracket" className="mt-8 relative">
            <div
                aria-hidden="true"
                className="absolute inset-x-0 -top-10 h-72 pointer-events-none"
                style={{ background: "radial-gradient(circle at top center, rgba(255,85,0,0.12) 0%, rgba(8,8,12,0) 70%)" }}
            />
            {data.team_count < 6 && (
                <p
                    data-testid="playoffs-note"
                    className="relative mb-6 text-sm text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-xl px-4 py-3"
                >
                    The full bracket locks in once 6 teams are registered ({data.team_count} now) — seeds below update
                    live from the standings.
                </p>
            )}
            <p className="relative text-xs text-slate-500 mb-8 text-center uppercase tracking-[0.2em]">
                Projected from live standings — top 2 seeds earn semifinal byes
            </p>

            <div className="hidden lg:block">
            <div className="relative grid grid-cols-[1fr_1fr_1.25fr_1fr_1fr] gap-6 items-stretch">
                <RoundColumn title="Quarterfinal" testid="bracket-col-qf1" topAnchor>
                    <MatchupBox match={data.quarterfinals[0]} testid="playoff-qf1" />
                </RoundColumn>

                <RoundColumn title="Semifinal" testid="bracket-col-sf1">
                    <ElbowLeft />
                    <div className="relative">
                        <Connector dir="right" />
                        <MatchupBox match={data.semifinals[0]} testid="playoff-sf1" />
                    </div>
                </RoundColumn>

                <div
                    data-testid="bracket-col-finals"
                    className="relative flex flex-col items-center justify-center min-h-[520px]"
                >
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[calc(50%+78px)] flex flex-col items-center gap-4">
                        <div data-testid="trophy-graphic" className="relative">
                            <div className="absolute inset-0 rounded-full bg-[#FFB800]/25 blur-2xl scale-[1.8] animate-pulse" />
                            <img
                                src="/trophy.png"
                                alt="YKPBL Championship Trophy"
                                className="relative w-28 h-auto sm:w-36 lg:w-40"
                                style={{ filter: "drop-shadow(0 0 22px rgba(255,184,0,0.45)) brightness(1.08) contrast(1.05)" }}
                            />
                        </div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#FFB800] whitespace-nowrap">
                            The Road Ends Here
                        </p>
                    </div>
                    <div className="relative w-full">
                        <Connector dir="left" />
                        <Connector dir="right" />
                        <MatchupBox match={data.finals} testid="playoff-finals" gold />
                    </div>
                    <p className="absolute top-[calc(50%+78px)] text-[10px] uppercase tracking-[0.25em] text-slate-500 text-center whitespace-nowrap">
                        One crown. Zero mercy.
                    </p>
                </div>

                <RoundColumn title="Semifinal" testid="bracket-col-sf2">
                    <div className="relative">
                        <Connector dir="left" />
                        <MatchupBox match={data.semifinals[1]} testid="playoff-sf2" />
                    </div>
                </RoundColumn>

                <RoundColumn title="Quarterfinal" testid="bracket-col-qf2" topAnchor>
                    <ElbowRight />
                    <MatchupBox match={data.quarterfinals[1]} testid="playoff-qf2" />
                </RoundColumn>
            </div>
            </div>

            <div className="lg:hidden" data-testid="playoff-bracket-mobile">
                <div className="grid grid-cols-2 gap-4">
                    <MatchupBox match={data.quarterfinals[0]} testid="playoff-qf1-mobile" />
                    <MatchupBox match={data.quarterfinals[1]} testid="playoff-qf2-mobile" />
                </div>
                <div className="grid grid-cols-2 gap-4" aria-hidden="true">
                    <span className="h-6 w-px bg-[#3E3E52] mx-auto" />
                    <span className="h-6 w-px bg-[#3E3E52] mx-auto" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <MatchupBox match={data.semifinals[0]} testid="playoff-sf1-mobile" />
                    <MatchupBox match={data.semifinals[1]} testid="playoff-sf2-mobile" />
                </div>
                <div className="relative h-12" aria-hidden="true">
                    <span className="absolute left-1/4 top-0 h-5 w-px -translate-x-1/2 bg-[#3E3E52]" />
                    <span className="absolute right-1/4 top-0 h-5 w-px translate-x-1/2 bg-[#3E3E52]" />
                    <span className="absolute left-1/4 right-1/4 top-5 h-px bg-[#3E3E52]" />
                    <span
                        className="absolute left-1/2 top-5 h-7 w-px -translate-x-1/2 bg-gradient-to-b from-[#3E3E52] to-[#FF5500]/70"
                        style={{ boxShadow: "0 0 6px rgba(255,85,0,0.3)" }}
                    />
                </div>
                <div className="flex flex-col items-center gap-4">
                    <div className="flex flex-col items-center gap-3">
                        <div data-testid="trophy-graphic-mobile" className="relative">
                            <div className="absolute inset-0 rounded-full bg-[#FFB800]/25 blur-2xl scale-[1.8] animate-pulse" />
                            <img
                                src="/trophy.png"
                                alt="YKPBL Championship Trophy"
                                className="relative w-32 h-auto"
                                style={{ filter: "drop-shadow(0 0 22px rgba(255,184,0,0.45)) brightness(1.08) contrast(1.05)" }}
                            />
                        </div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#FFB800]">
                            The Road Ends Here
                        </p>
                    </div>
                    <div className="w-full max-w-sm">
                        <MatchupBox match={data.finals} testid="playoff-finals-mobile" gold />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 text-center">
                        One crown. Zero mercy.
                    </p>
                </div>
            </div>
        </div>
    );
}
